import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Truck, Package, Car, Home, Wrench, Users, Building2, CheckCircle2, ArrowRight, Star, Shield, Clock, MapPin, Baby, Sparkles, ChefHat, Snowflake, TreePine, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("Detecting location...");

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocation("Ontario, Canada");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&zoom=5`);
          const data = await res.json();
          const addr = data.address || {};
          const region = addr.state || addr.province || addr.region || addr.county || "";
          const country = addr.country || "";
          setLocation(region && country ? `${region}, ${country}` : country || "Ontario, Canada");
        } catch {
          setLocation("Ontario, Canada");
        }
      },
      () => setLocation("Ontario, Canada"),
      { timeout: 5000 }
    );
  }, []);

  const suggestions = [
    { icon: Truck, title: "Cargo", desc: "Move furniture & large items with helpers.", href: "/cargo", color: "bg-lime-400", keywords: ["furniture", "appliance", "heavy", "delivery", "move"] },
    { icon: Package, title: "Courier", desc: "Fast package delivery, documents to parcels.", href: "/courier", color: "bg-sky-500", keywords: ["package", "document", "parcel", "express", "send"] },
    { icon: Car, title: "Towing", desc: "24/7 towing for breakdowns & relocations.", href: "/towing", color: "bg-orange-500", keywords: ["tow", "breakdown", "car", "vehicle", "accident"] },
    { icon: Home, title: "Movers", desc: "Full-service moving for home & office.", href: "/movers", color: "bg-purple-500", keywords: ["move", "relocate", "home", "office", "packing"] },
    { icon: Wrench, title: "Roadside", desc: "Battery, tire, fuel & lockout help fast.", href: "/roadside-assistance", color: "bg-red-500", keywords: ["battery", "tire", "fuel", "lockout", "flat", "jump", "gas"] },
    { icon: Baby, title: "Nanny", desc: "Private childcare at your home.", href: "/nanny", color: "bg-pink-500", keywords: ["child", "baby", "babysitter", "care", "kids"] },
    { icon: Sparkles, title: "Cleaning", desc: "Professional home & office cleaning.", href: "/cleaning", color: "bg-cyan-500", keywords: ["clean", "house", "maid", "deep", "sanitize"] },
    { icon: ChefHat, title: "Private Chef", desc: "Book a personal chef for any event.", href: "/private-chef", color: "bg-amber-500", keywords: ["chef", "cook", "food", "meal", "event", "dinner", "catering"] },
    { icon: TreePine, title: "Lawn Mowing", desc: "Mowing, trimming & yard cleanup.", href: "/lawn-mowing", color: "bg-green-600", keywords: ["lawn", "grass", "yard", "garden", "trim", "landscape"] },
    { icon: Snowflake, title: "Snow Plowing", desc: "Driveways, walkways & property cleared.", href: "/snow-plowing", color: "bg-sky-600", keywords: ["snow", "plow", "ice", "driveway", "winter", "shovel"] },
    { icon: Users, title: "Laborer", desc: "Loading, packing, assembly & more.", href: "/laborer", color: "bg-rose-500", keywords: ["labor", "help", "load", "assemble", "pack", "worker", "manual"] },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navbar />

      {/* Hero Section - Uber-style split layout */}
      <section className="bg-white pt-24 md:pt-28 pb-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[480px]">
            {/* Left: Content + Quick Book */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium text-emerald-600 mb-3 flex items-center gap-2" data-testid="hero-location">
                <MapPin className="w-4 h-4" /> {location}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-emerald-950 leading-tight mb-6" data-testid="hero-heading">
                Get anything done with <span className="text-lime-500">Stack</span>
              </h1>
              <p className="text-base text-stone-500 mb-8 max-w-lg leading-relaxed">
                Book your first service today and experience the Stack difference. Fast. Reliable. Affordable.
              </p>

              {/* Search / Quick Book */}
              <div className="bg-stone-50 rounded-2xl p-5 border border-stone-100 max-w-md relative" data-testid="hero-quick-book">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="text"
                    placeholder="What do you need help with?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3.5 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 placeholder:text-stone-400"
                    data-testid="hero-search-input"
                  />
                </div>

                {/* Search results dropdown */}
                {searchQuery.trim().length > 0 && (
                  <div className="absolute left-0 right-0 top-[72px] mx-5 bg-white rounded-xl border border-stone-200 shadow-lg z-20 max-h-64 overflow-y-auto" data-testid="search-results">
                    {suggestions
                      .filter((s) => {
                        const q = searchQuery.toLowerCase();
                        return s.title.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q) || s.keywords?.some(k => k.includes(q));
                      })
                      .map((s) => (
                        <Link
                          key={s.href}
                          to={s.href}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors border-b border-stone-50 last:border-0"
                          data-testid={`search-result-${s.title.toLowerCase()}`}
                        >
                          <div className={`w-8 h-8 ${s.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <s.icon className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-emerald-950">{s.title}</p>
                            <p className="text-xs text-stone-400">{s.desc}</p>
                          </div>
                        </Link>
                      ))
                    }
                    {suggestions.filter((s) => {
                      const q = searchQuery.toLowerCase();
                      return s.title.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q) || s.keywords?.some(k => k.includes(q));
                    }).length === 0 && (
                      <div className="px-4 py-4 text-sm text-stone-400 text-center" data-testid="no-search-results">
                        No services match "{searchQuery}"
                      </div>
                    )}
                  </div>
                )}

                {/* Quick tags (shown when not searching) */}
                {searchQuery.trim().length === 0 && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    {["Cargo", "Courier", "Movers", "Cleaning", "Towing"].map((s) => (
                      <Link
                        key={s}
                        to={`/${s.toLowerCase()}`}
                        className="px-3 py-1.5 bg-white border border-stone-200 rounded-full text-xs font-medium text-stone-600 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                        data-testid={`quick-tag-${s.toLowerCase()}`}
                      >
                        {s}
                      </Link>
                    ))}
                  </div>
                )}

                <a href="#suggestions">
                  <Button className="w-full mt-4 bg-emerald-950 hover:bg-emerald-900 text-white rounded-xl py-6 font-semibold text-sm" data-testid="hero-book-btn">
                    Browse All Services <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Right: Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-3xl overflow-hidden aspect-[4/3] relative">
                <img
                  src="https://images.unsplash.com/photo-1551825687-f9de1603ed8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMHBlcnNvbiUyMHNtaWxpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc1ODQwMDgzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Stack Professional"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-between">
                  <p className="text-white font-semibold text-lg">Ready to get started?</p>
                  <Link to="/business">
                    <Button className="bg-white text-emerald-950 hover:bg-stone-100 rounded-full px-5 py-2 text-sm font-semibold" data-testid="hero-business-btn">
                      For Business
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Suggestions Row - Uber style */}
      <section id="suggestions" className="bg-white pb-12 pt-4" data-testid="suggestions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-emerald-950 mb-5">Suggestions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {suggestions.map((item, i) => (
              <motion.div
                key={item.href}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.06 }}
              >
                <Link
                  to={item.href}
                  className="block bg-stone-50 rounded-2xl p-4 hover:bg-stone-100 transition-colors group h-full border border-stone-100 hover:border-emerald-200"
                  data-testid={`suggestion-${item.title.toLowerCase()}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-emerald-950 text-sm">{item.title}</h3>
                      <p className="text-xs text-stone-500 mt-1 leading-relaxed">{item.desc}</p>
                    </div>
                    <div className={`w-10 h-10 ${item.color} rounded-xl flex items-center justify-center flex-shrink-0 ml-2 group-hover:scale-105 transition-transform`}>
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Section */}
      <section className="py-20 bg-white" data-testid="b2b-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium text-emerald-600 mb-3">For Businesses</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-emerald-950 leading-tight mb-5">
                Scale Your Logistics Without the Overhead
              </h2>
              <p className="text-base text-stone-500 mb-8 leading-relaxed">
                Whether you need a delivery fleet, cargo transport, courier services, towing partners, or moving crews — Stack delivers reliable, on-demand logistics solutions tailored for businesses of every size.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "All services available for business",
                  "Volume pricing & dedicated support",
                  "API integration available",
                  "Proof of delivery & reporting"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-950" />
                    </div>
                    <span className="text-sm text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/business">
                <Button className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-xl px-8 py-6 font-semibold text-sm" data-testid="b2b-cta-btn">
                  Get a Custom Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/6068761/pexels-photo-6068761.jpeg"
                alt="Business Logistics"
                className="rounded-3xl shadow-xl w-full"
              />
              <div className="absolute -bottom-5 -left-5 bg-white/90 backdrop-blur-lg border border-stone-200 shadow-xl rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-bold text-emerald-950 text-sm">200+</div>
                    <div className="text-xs text-stone-500">Business Partners</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-stone-50" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium text-emerald-600 mb-3">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-emerald-950">Built for Trust & Reliability</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "Fully Insured", description: "Every job is covered by our comprehensive insurance policy." },
              { icon: Clock, title: "On-Time Guarantee", description: "We deliver when we say we will, or your next service is discounted." },
              { icon: MapPin, title: "Real-Time Tracking", description: "Know exactly where your driver is at every moment." }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-stone-100 text-center hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-emerald-950 mb-2">{item.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-lime-400" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-emerald-950 mb-5">Ready to Get Started?</h2>
            <p className="text-base text-emerald-800 mb-8 max-w-xl mx-auto leading-relaxed">
              Book your first service today and experience the Stack difference.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#suggestions">
                <Button className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-xl px-8 py-6 font-semibold text-sm" data-testid="final-cta-btn">
                  <Truck className="w-4 h-4 mr-2" />
                  Book a Service
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
